/**
 * Hard ceiling for AuthView cold-boot — users must never see an unbounded spinner.
 * Bundle marker: boot_deadline_15s_v1 (grep deployed JS to verify).
 */
import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  BOOT_DEADLINE_MARKER,
  BOOT_DEADLINE_MS,
  shouldApplyBootDeadline,
} from './bootDeadline';
import { hasReachedUsableAuthedState } from './authTerminalLatch';

export const AUTH_INIT_CEILING_MS = BOOT_DEADLINE_MS;
/** @deprecated use BOOT_DEADLINE_MARKER — kept for grep continuity */
export const AUTH_INIT_TIMEOUT_MARKER = 'auth_init_timeout_ceiling_v1';

export type AuthInitForceTimeout = () => void;

let pendingStep = 'boot';
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
let forceTimeoutHandler: AuthInitForceTimeout | null = null;
/** Absolute boot anchor — must not reset during restore retry flicker. */
let bootStartedAt: number | null = null;
let timeoutFired = false;
/** Watchdog is inert until startAuthInitWatchdog() — never arm on bare module import. */
let watchdogEnabled = false;

export function getAuthInitPendingStep(): string {
  return pendingStep;
}

export function setAuthInitStep(step: string): void {
  pendingStep = step;
}

export function registerAuthInitForceTimeout(handler: AuthInitForceTimeout): void {
  forceTimeoutHandler = handler;
}

function fireAuthInitTimeout(): void {
  if (timeoutFired) return;
  if (!shouldApplyBootDeadline()) {
    if (watchdogTimer) clearTimeout(watchdogTimer);
    const now = Date.now();
    if (bootStartedAt) {
      const elapsed = now - bootStartedAt;
      const remaining = Math.max(BOOT_DEADLINE_MS - elapsed, 500);
      watchdogTimer = setTimeout(fireAuthInitTimeout, remaining);
    }
    return;
  }
  timeoutFired = true;
  watchdogTimer = null;
  try {
    const step = pendingStep || 'unknown';
    console.warn(`[js:auth] boot_deadline pending_step=${step} marker=${BOOT_DEADLINE_MARKER}`);
    diagnosticLog('warn', 'other', 'auth_init_timeout', {
      pending_step: step,
      marker: BOOT_DEADLINE_MARKER,
      ceiling_ms: BOOT_DEADLINE_MS,
    });
  } catch { /* diagnostic only */ }
  forceTimeoutHandler?.();
}

function syncAuthInitWatchdog(isLoading: boolean): void {
  if (!isLoading) {
    if (hasReachedUsableAuthedState() || timeoutFired) {
      bootStartedAt = null;
      if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
    }
    return;
  }

  const now = Date.now();
  if (!bootStartedAt) bootStartedAt = now;
  const elapsed = now - bootStartedAt;
  if (elapsed >= BOOT_DEADLINE_MS) {
    fireAuthInitTimeout();
    return;
  }

  if (watchdogTimer) clearTimeout(watchdogTimer);
  watchdogTimer = setTimeout(fireAuthInitTimeout, BOOT_DEADLINE_MS - elapsed);
}

/** Begin bootstrap ceiling tracking — call from AppLayout (or login shell) on mount, not at import. */
export function startAuthInitWatchdog(isLoading = true): void {
  watchdogEnabled = true;
  bootStartedAt = null;
  timeoutFired = false;
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
  syncAuthInitWatchdog(isLoading);
}

/** Tear down watchdog timers — test teardown and route unmount. */
export function stopAuthInitWatchdog(): void {
  watchdogEnabled = false;
  bootStartedAt = null;
  timeoutFired = false;
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
}

export function armAuthInitWatchdog(isLoading: boolean): void {
  if (!watchdogEnabled) return;
  syncAuthInitWatchdog(isLoading);
}

export function resetAuthInitWatchdogForTests(): void {
  stopAuthInitWatchdog();
  pendingStep = 'boot';
  forceTimeoutHandler = null;
}

async function fetchAuthJsonWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs = BOOT_DEADLINE_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** POST /api/auth/refresh with abort — restoreFromAuthJson must not pend forever. */
export async function refreshAuthJsonSession(
  refreshToken: string,
  timeoutMs = BOOT_DEADLINE_MS,
): Promise<Response> {
  return fetchAuthJsonWithTimeout(
    'https://api.maestroaudio.net/api/auth/refresh',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    },
    timeoutMs,
  );
}
